// Versão: 1.00
// Data..: 2026-09-24
// Diálogos da aplicação (confirmação e prompt) que substituem window.confirm/prompt.
// Renderizados como Modal padrão (Esc, clicar fora, X, Fechar). Retornam Promise.

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Modal } from '../components/Modal.js';

interface ConfirmOptions {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean; // destaca o botão de confirmação como ação destrutiva
}

interface PromptOptions {
  title?: string;
  message?: ReactNode;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
}

interface DialogContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

const DialogContext = createContext<DialogContextValue>({
  confirm: async () => false,
  prompt: async () => null,
});

type ConfirmState = ConfirmOptions & { resolve: (v: boolean) => void };
type PromptState = PromptOptions & { resolve: (v: string | null) => void };

export function DialogProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [promptState, setPromptState] = useState<PromptState | null>(null);
  const [promptValue, setPromptValue] = useState('');

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => setConfirmState({ ...opts, resolve }));
  }, []);

  const prompt = useCallback((opts: PromptOptions) => {
    setPromptValue(opts.defaultValue ?? '');
    return new Promise<string | null>((resolve) => setPromptState({ ...opts, resolve }));
  }, []);

  const closeConfirm = (value: boolean) => {
    confirmState?.resolve(value);
    setConfirmState(null);
  };
  const closePrompt = (value: string | null) => {
    promptState?.resolve(value);
    setPromptState(null);
  };

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}

      {/* Diálogo de confirmação */}
      <Modal
        open={!!confirmState}
        title={confirmState?.title ?? 'Confirmar'}
        onClose={() => closeConfirm(false)}
        maxWidth={420}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => closeConfirm(false)}>
              {confirmState?.cancelLabel ?? 'Cancelar'}
            </button>
            <button
              className={`btn ${confirmState?.danger ? 'btn-danger' : 'btn-primary'}`}
              onClick={() => closeConfirm(true)}
              autoFocus
            >
              {confirmState?.confirmLabel ?? 'Confirmar'}
            </button>
          </>
        }
      >
        <div style={{ lineHeight: 1.6 }}>{confirmState?.message}</div>
      </Modal>

      {/* Diálogo de entrada de texto (prompt) */}
      <Modal
        open={!!promptState}
        title={promptState?.title ?? 'Informe um valor'}
        onClose={() => closePrompt(null)}
        maxWidth={440}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => closePrompt(null)}>Cancelar</button>
            <button className="btn btn-primary" onClick={() => closePrompt(promptValue)}>
              {promptState?.confirmLabel ?? 'Confirmar'}
            </button>
          </>
        }
      >
        {promptState?.message && <p className="text-muted mb-2">{promptState.message}</p>}
        <div className="field mb-0">
          {promptState?.label && <label className="label">{promptState.label}</label>}
          <input
            className="input"
            autoFocus
            value={promptValue}
            placeholder={promptState?.placeholder}
            onChange={(e) => setPromptValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') closePrompt(promptValue); }}
          />
        </div>
      </Modal>
    </DialogContext.Provider>
  );
}

export const useDialog = () => useContext(DialogContext);
